import { useQuery, UseQueryResult } from "@tanstack/react-query";
import axiosInstance from "../common/axiosInstance";
import { LevelAccessType } from "../../utils/types";

export const PATHS = Object.freeze({
  POST_GLOBAL_SEARCH_RESULTS: "/global-search",
  POST_KEYWORD_SEARCH_RESULTS: "/keyword-search",
  POST_CLIENT_KEYWORD_SEARCH_RESULTS: "/client/keyword-search",
  GET_ASK_QUERY: "/askai",
  ASK_7G_INTERACTION_LEVEL: "/interactions/ask_7g_interaction",
  GET_CLIENTS_WITH_ASK_7G_HISTORY: "/interactions/clients-with-ask-7g-history",
  ASK_7G_HISTORY: "/interactions/ask7g-history",
});

export interface PostGlobalSearchParams {
  search_text: string;
  employeePorId: string;
  isExternal: boolean;
  startTime: string;
  endTime: string;
}
export interface KeywordSearchParams {
  searchText: string;
  limit: number;
  offset: number;
  sortOrder: string;
  sortBy: string;
  startTime: string;
  endTime: string;
  employeeEmail: string;
  timeZone?: string;
  clientSubOrgId?: string;
}

export interface AskQueryParams {
  query: string;
}

const getAskQuery = async (
  params: AskQueryParams
): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ASK_QUERY, {params});
  return response.data;
};

export interface ClientsWithAsk7GHistoryParams {
  levelType: string;
}

export interface Ask7GHistoryParams {
  levelType: string;
  levelId?: string;
  subLevelId?: string;
  interactionId?: string;
}
export {getAskQuery};

interface Ask7GInteractionParams {
  interactionId?: string;
  query: string;
  clientDomain?: string;
  orgId?: string;
  subOrgId?: string;
  levelAccessType?: LevelAccessType;
}
const postGlobalSearchResults = async (
  params: PostGlobalSearchParams
): Promise<any> => {
  const queryParams = new URLSearchParams({
    search_text: params.search_text,
  });

  const response = await axiosInstance.post(
    `${PATHS.POST_GLOBAL_SEARCH_RESULTS}?${queryParams.toString()}`,
    {
      employeePorId: params.employeePorId,
      isExternal: params.isExternal,
      startTime: params.startTime,
      endTime: params.endTime,
    }
  );

  const actionItemUpdated = JSON.parse(
    sessionStorage.getItem("actionItemUpdated") || "false"
  );

  if (actionItemUpdated) {
    if (response) {
      sessionStorage.setItem("globalSearchPostResults", JSON.stringify(response));
    }
    window.dispatchEvent(new Event("actionItemUpdated"));
  }

  return response;
};

const useGetGlobalSearchResults = (
  params: PostGlobalSearchParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const key = `${params.search_text}_${params.employeePorId}`;
  return useQuery({
    queryKey: ["post_global_search_results", key],
    queryFn: () => postGlobalSearchResults(params),
    enabled,
    retry: 0,
  });
};

const postKeywordSearchResults = async (
  params: KeywordSearchParams
): Promise<any> => {
  const queryParams = new URLSearchParams({
    ...(params.searchText && {searchText: params.searchText,}),
    limit: String(params.limit),
    offset: String(params.offset),
    sortOrder: params.sortOrder,
    sortBy: params.sortBy,
    timeZone: String(params.timeZone),
  }
);
const response = await axiosInstance.post(
    `${PATHS.POST_KEYWORD_SEARCH_RESULTS}?${queryParams.toString()}`,
    {
      startTime: params.startTime,
      endTime: params.endTime,
      employeeEmail: params.employeeEmail,
      ...(params.clientSubOrgId && {clientSubOrgId: params.clientSubOrgId,}),
    }
  );
  return response.data;
};

const useGetKeywordSearchResults = (
  params: KeywordSearchParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const key = JSON.stringify(params);
  return useQuery({
    queryKey: ["post_keyword_search_results", key],
    queryFn: () => postKeywordSearchResults(params),
    enabled,
    retry: 0,
  });
};

const postClientKeywordSearchResults = async (
  params: KeywordSearchParams
): Promise<any> => {
  const queryParams = new URLSearchParams({
    ...(params.searchText && {searchText: params.searchText,}),
    limit: String(params.limit),
    offset: String(params.offset),
    sortOrder: params.sortOrder,
    sortBy: params.sortBy,
  }
);
const response = await axiosInstance.post(
    `${PATHS.POST_CLIENT_KEYWORD_SEARCH_RESULTS}?${queryParams.toString()}`,
    {
      startTime: params.startTime,
      endTime: params.endTime,
      employeeEmail: params.employeeEmail,
    }
  );
  return response.data;
};

const useGetClientKeywordSearchResults = (
  params: KeywordSearchParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const key = JSON.stringify(params);
  return useQuery({
    queryKey: ["post_client_keyword_search_results", key],
    queryFn: () => postClientKeywordSearchResults(params),
    enabled,
    retry: 0,
  });
};

const useGetAskQuery = (
  params: AskQueryParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  const key = `${params.query}`;
  return useQuery({
    queryKey: ["get_ask_query", key],
    queryFn: () => getAskQuery(params),
    enabled,
    retry: 0,
  });
};

const getAsk7GInteractionQuery = async (params: Ask7GInteractionParams) => {
  const filteredParams = {
    query: params.query,
    ...(params.interactionId && { interactionId: params.interactionId }),
    ...(params.clientDomain && { clientDomain: params.clientDomain }),
    ...(params.orgId && { orgId: params.orgId }),
    ...(params.subOrgId && { subOrgId: params.subOrgId }),
    ...(params.levelAccessType && { levelAccessType: params.levelAccessType }),
  };

  return axiosInstance.get(PATHS.ASK_7G_INTERACTION_LEVEL, {
    params: filteredParams,
  });
};

const useGetAsk7GInteractionQuery = (
  params: Ask7GInteractionParams,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ["ask7g_interaction_query", params],
    queryFn: () => getAsk7GInteractionQuery(params),
    enabled,
    retry: 0,
    staleTime: 0,
    gcTime: 0,  
  });
};

const getClientsWithAsk7GHistory = async (
  params: ClientsWithAsk7GHistoryParams
): Promise<any> => {
  const response = await axiosInstance.get(
    PATHS.GET_CLIENTS_WITH_ASK_7G_HISTORY,
    {
      params,
    }
  );

  return response.data;
};

const useGetClientsWithAsk7GHistory = (
  params: ClientsWithAsk7GHistoryParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["clients_with_ask7g_history", params],
    queryFn: () => getClientsWithAsk7GHistory(params),
    enabled,
    retry: 0,
  });
};

const getAsk7GHistory = async (
  params: Ask7GHistoryParams
): Promise<any> => {
  const filteredParams = {
    levelType: params.levelType,
    ...(params.levelId && { levelId: params.levelId }),
    ...(params.subLevelId && { subLevelId: params.subLevelId }),
    ...(params.interactionId && { interactionId: params.interactionId }),
  };

  const response = await axiosInstance.get(
    PATHS.ASK_7G_HISTORY,
    {
      params: filteredParams,
    }
  );

  return response.data;
};

const useGetAsk7GHistory = (
  params: Ask7GHistoryParams,
  enabled: boolean
): UseQueryResult<any, any> => {
  return useQuery({
    queryKey: ["ask7g_history", params],
    queryFn: () => getAsk7GHistory(params),
    enabled,
    retry: 0,
  });
};
export interface GlobalSearchService {
  readonly useGetGlobalSearchResults: (
    params: PostGlobalSearchParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetKeywordSearchResults: (
    params: KeywordSearchParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetClientKeywordSearchResults: (
    params: KeywordSearchParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetAskQuery: (
    params: AskQueryParams,
    enable: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetAsk7GInteractionQuery: (
    params: Ask7GInteractionParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetClientsWithAsk7GHistory: (
    params: ClientsWithAsk7GHistoryParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  
  readonly useGetAsk7GHistory: (
    params: Ask7GHistoryParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
}

const GlobalSearchService: GlobalSearchService = Object.freeze({
  useGetGlobalSearchResults,
  useGetKeywordSearchResults,
  useGetClientKeywordSearchResults,
  useGetAskQuery,
  useGetAsk7GInteractionQuery,
  useGetClientsWithAsk7GHistory,
  useGetAsk7GHistory,
});

export default GlobalSearchService;
